import React, { useCallback, useEffect, useState } from "react";
import "../../pages/Home.css";
import CustomEditor, { ErrorBoundary } from "./IntegrationEditor";
import InfoIcon from "@mui/icons-material/Info";
import {
  FormControlLabel,
  Paper,
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  RadioGroup,
  Radio,
  Alert,
  Slide,
  Snackbar,
  Stepper,
  Step,
  StepLabel,
  Backdrop,
  DialogActions,
  Tooltip,
} from "@mui/material";

import SuperAppButton from "../SuperAppButton";
import { CardPreview, ItineraryWidget } from "./integrationUtils";
import CircularProgress from "@mui/material/CircularProgress";
import DynamicJsonEditorV1 from "./DynamicJsonEditorV1";
import { uiExamples } from "../../utils/widgetExamples";
import { Info, Settings, Api, Palette, Widgets } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import { styled } from "@mui/material/styles";
import APICallUI from "./IntegrationApiConfig";
import MCPWorkflow from "./IntegrationMCPConfig";

import ApiSelector from "./Apilisting";


const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage:
        "linear-gradient( 95deg,rgb(242,113,33) 0%,rgb(233,64,87) 50%,rgb(138,35,135) 100%)",
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage:
        "linear-gradient( 95deg,rgb(242,113,33) 0%,rgb(233,64,87) 50%,rgb(138,35,135) 100%)",
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: "#eaeaf0",
    borderRadius: 1,
    ...theme.applyStyles("dark", {
      backgroundColor: theme.palette.grey[800],
    }),
  },
}));

function SlideTransition(props) {
  return <Slide {...props} direction="up" />;
}

export function convertSchema1ToSchema2All(input, options = {}) {
  const endpoints = Array.isArray(input?.data) ? input.data : [];
  if (endpoints.length === 0) return [];

  const main = convertOneEndpoint(endpoints[0], options);

  if (endpoints.length > 1) {
    main.subApis = endpoints
      .slice(1)
      .map((ep) => convertOneEndpoint(ep, options));
  }
  return [main];
}

function convertOneEndpoint(endpoint, options = {}) {
  const DEFAULTS = {
    includeNulls: true,
    position: { x: 0, y: 0 },
    isLooping: false,
    selectedPath: "",
    loopCount: "1",
    applyMentionTo: { params: true, payload: true, headers: false, url: false },
  };
  const cfg = { ...DEFAULTS, ...options };

  // ---------- helpers (ONLY target VALUE, data, type, name for recursion) ----------
  const isPlainObject = (v) =>
    v !== null && typeof v === "object" && !Array.isArray(v);
  const includeField = (node) => !Boolean(node?.isDefault); // used for payload recursion only
  const encodeNodes = (nodes) =>
    JSON.stringify(nodes ?? [{ type: "text", text: "" }]);
  const textNode = (s) => ({ type: "text", text: String(s ?? "") });
  const toRichText = (str) => encodeNodes([textNode(str)]);
  const ensureUpper = (m) => String(m || "").toUpperCase();
  const joinUrl = (base, path) => {
    const b = String(base || "").replace(/\/+$/g, "");
    const p = String(path || "").replace(/^\/+/g, "");
    return `${b}/${p}`;
  };
  const MENTION_PREFIX = "input_parameters.";
  const makeMentionByName = (name) => {
    const base = String(name ?? "");
    const prefixed = base.toLowerCase().startsWith(MENTION_PREFIX.toLowerCase())
      ? base // avoid double prefix if already present
      : `${MENTION_PREFIX}${base}`;

    return {
      type: "mention",
      attrs: {
        id: prefixed,
        label: prefixed + " ",
        mentionSuggestionChar: "@",
      },
    };
  };

  // ---------- CORE: schema1 VALUE/data recursion ----------
  function parseValue(valueNode, declaredType) {
    if (
      valueNode === null ||
      typeof valueNode === "string" ||
      typeof valueNode === "number" ||
      typeof valueNode === "boolean"
    )
      return valueNode;

    if (Array.isArray(valueNode)) {
      return valueNode.map((v) => parseNode(v)).filter((v) => v !== undefined);
    }

    if (isPlainObject(valueNode) && Array.isArray(valueNode.data)) {
      const t = String(declaredType || valueNode.type || "").toLowerCase();
      if (t === "array") {
        return valueNode.data
          .filter(includeField)
          .map((child) => parseNode(child))
          .filter((v) => v !== undefined);
      }
      return buildObjectFromDataArray(valueNode.data);
    }

    if (isPlainObject(valueNode)) {
      const out = {};
      for (const [k, v] of Object.entries(valueNode))
        out[k] = parseValue(v, undefined);
      return out;
    }
    return valueNode;
  }

  function parseNode(node) {
    if (!node || typeof node !== "object") return node;
    if (!includeField(node)) return undefined; // skip isDefault in payload recursion

    if ("VALUE" in node) return parseValue(node.VALUE, node.type);
    if ("data" in node && Array.isArray(node.data))
      return buildObjectFromDataArray(node.data);
    return node;
  }

  // [{ name, VALUE?, type?, data? }, ...] → { name: parsed(VALUE/data), ... }
  function buildObjectFromDataArray(arr) {
    const obj = {};
    for (const item of arr || []) {
      if (!item || !isPlainObject(item) || !includeField(item)) continue;
      const key = item.name ?? item.key ?? item.id;
      if (!key) continue;
      const val = parseNode(item);
      if (val === undefined) continue;
      obj[key] = val;
    }
    return obj;
  }

  function jsonNodesWithMentionSkeleton(value, keyPath = []) {
    const nodes = [];
    function append(val, kp) {
      if (Array.isArray(val)) {
        nodes.push(textNode("["));
        val.forEach((el, i) => {
          if (isPlainObject(el) || Array.isArray(el)) {
            append(el, kp);
          } else {
            const parentName = kp[kp.length - 1] || "";
            nodes.push(makeMentionByName(parentName));
          }
          if (i < val.length - 1) nodes.push(textNode(","));
        });
        nodes.push(textNode("]"));
      } else if (isPlainObject(val)) {
        const entries = Object.entries(val);
        nodes.push(textNode("{"));
        entries.forEach(([k, v], i) => {
          nodes.push(textNode(`"${k}":`));
          if (isPlainObject(v) || Array.isArray(v)) {
            append(v, [...kp, k]);
          } else {
            nodes.push(makeMentionByName(k));
          }
          if (i < entries.length - 1) nodes.push(textNode(","));
        });
        nodes.push(textNode("}"));
      } else {
        const name = kp[kp.length - 1] || "";
        nodes.push(makeMentionByName(name));
      }
    }
    append(value, keyPath);
    return nodes;
  }

  // headers/params mapping
  function mapKeyVals(kvArray = [], scope) {
    const out = [];
    for (const n of kvArray) {
      if (!n || !isPlainObject(n)) continue;

      const key = n.name ?? n.key ?? n.id;
      if (!key) continue;

      // --- isDefault === true → fixed shape; VALUE/value stringified into value ---
      if (n.isDefault === true) {
        const rawVal = n.VALUE !== undefined ? n.VALUE : n.value; // support VALUE or value
        const valueString = rawVal !== undefined ? rawVal : "";
        out.push({
          isDefault: true,
          key: toRichText(key),
          path: toRichText(key),
          value: toRichText(valueString),
        });
        continue;
      }

      // Non-defaults → normal behavior
      const rawVal = parseNode(n);
      let valueField;

      if (scope === "params" && options?.applyMentionTo?.params !== false) {
        const nodes =
          isPlainObject(rawVal) || Array.isArray(rawVal)
            ? jsonNodesWithMentionSkeleton(rawVal, [key])
            : [makeMentionByName(key)];
        valueField = encodeNodes(nodes);
      } else {
        const strVal =
          isPlainObject(rawVal) || Array.isArray(rawVal)
            ? JSON.stringify(rawVal)
            : rawVal == null
            ? ""
            : String(rawVal);
        valueField = toRichText(strVal);
      }

      out.push({
        key: toRichText(key),
        value: valueField,
        isDefault: false,
        path: toRichText(key),
        required: Boolean(n.required),
      });
    }
    return out;
  }

  // --------- build one schema2 node ---------
  const urlStr = joinUrl(endpoint.baseUrl, endpoint.path);
  const method = ensureUpper(endpoint.method);

  const headers = mapKeyVals(endpoint.headers || [], "headers");
  const params = mapKeyVals(endpoint.params || [], "params");

  const payloadObject = buildObjectFromDataArray(endpoint.payload || []);
  const payloadNodes =
    options?.applyMentionTo?.payload !== false
      ? jsonNodesWithMentionSkeleton(payloadObject, [])
      : [textNode(JSON.stringify(payloadObject))];

  return {
    url: toRichText(urlStr),
    method,
    headers,
    params,
    payload: [{ json: encodeNodes(payloadNodes) }],
    subApis: [], // filled by convertSchema1ToSchema2All if multiple endpoints
    responsePath: "",
    isLooping: cfg.isLooping,
    selectedPath: cfg.selectedPath,
    loopCount: cfg.loopCount,
    position: cfg.position,
  };
}

const StepContent = ({
  title,
  description,
  onNext,
  onBack,
  onSkip,
  lastStep,
  isStepLoading,
  loadingMessages,
  activeStep,
  isLastStep,
  isSkip,
  component,
  isDisabled,
}) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  useEffect(() => {
    let interval;
    if (isStepLoading) {
      interval = setInterval(() => {
        setCurrentMessageIndex(
          (prevIndex) => (prevIndex + 1) % loadingMessages.length
        );
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isStepLoading, loadingMessages]);

  return (
    <motion.div
      style={{
        padding: "32px",
        position: "relative",
        borderRadius: "8px",
      }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
    >
      <h2
        style={{
          fontSize: "1.5rem",
          fontWeight: "600",
          color: "#E0E7FF",
          marginBottom: "16px",
          textAlign: "center",
        }}
      >
        {title}
      </h2>
      <p
        style={{
          color: "#A3BFFA",
          marginBottom: "24px",
          lineHeight: "1.6",
          fontSize: "1rem",
          textAlign: "center",
        }}
      >
        {description}
      </p>

      {component ? component : null}
      {lastStep && (
        <p
          style={{
            fontSize: "0.875rem",
            color: "#9CA3AF",
            marginBottom: "24px",
            textAlign: "center",
          }}
        >
          Previous Step: {lastStep}
        </p>
      )}
      <div
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "20px",
        }}
      >
        <div style={{ flex: 1 }}>
          {lastStep && (
            <motion.div>
              <SuperAppButton
                variant="contained"
                onClick={onBack}
                style={{
                  backgroundColor: "#6B7280",
                  color: "#E0E7FF",
                  textTransform: "none",
                  fontWeight: "500",
                  // padding: "8px 16px",
                  borderRadius: "20px",
                  "&:hover": { backgroundColor: "#4B5563" },
                }}
              >
                Back
              </SuperAppButton>
            </motion.div>
          )}
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          {isSkip && !isLastStep && (
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <SuperAppButton
                variant="contained"
                onClick={onSkip}
                style={{
                  backgroundColor: "#6B7280",
                  color: "#E0E7FF",
                  textTransform: "none",
                  fontWeight: "500",
                  // padding: "8px 16px",
                  borderRadius: "20px",
                  "&:hover": { backgroundColor: "#4B5563" },
                }}
              >
                Skip
              </SuperAppButton>
            </motion.div>
          )}
          {!isLastStep && (
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <SuperAppButton
                variant="contained"
                onClick={onNext}
                // style={{
                //   // backgroundColor: "#7C3AED",
                //   color: "#E0E7FF",
                //   textTransform: "none",
                //   fontWeight: "500",
                //   padding: "8px 16px",
                //   borderRadius: "20px",
                //   "&:hover": { backgroundColor: "#6B21A8" },
                //   "&:disabled": {
                //     backgroundColor: "#6B7280",
                //     color: "#9CA3AF",
                //   },
                // }}
                // disabled={isStepLoading}
                disabled={!isDisabled ? !isDisabled : false}
              >
                Next
              </SuperAppButton>
            </motion.div>
          )}
          {/* {isLastStep && (
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                variant="contained"
                onClick={onNext}
                sx={{
                  backgroundColor: "#10B981",
                  color: "#E0E7FF",
                  textTransform: "none",
                  fontWeight: "500",
                  padding: "8px 16px",
                  borderRadius: "20px",
                  "&:hover": { backgroundColor: "#059669" },
                  "&:disabled": {
                    backgroundColor: "#4B5563",
                    color: "#9CA3AF",
                  },
                }}
                disabled={isStepLoading}
              >
                Finish
              </Button>
            </motion.div>
          )} */}
        </div>
      </div>
      <Backdrop
        sx={{
          color: "#E0E7FF",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(30, 42, 68, 0.8)",
        }}
        open={isStepLoading}
        onClick={() => {}}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3 }}
          style={{ display: "flex", alignItems: "center" }}
        >
          <CircularProgress color="inherit" sx={{ marginRight: "16px" }} />
          <Typography variant="h6" color="inherit">
            {loadingMessages[currentMessageIndex]}
          </Typography>
        </motion.div>
      </Backdrop>
    </motion.div>
  );
};

function limitArrayElements(input, limit = 4, seen = new WeakSet()) {
  // Handle null or undefined
  if (input == null) return input;

  // Handle circular references
  if (typeof input === "object" && input !== null) {
    if (seen.has(input)) return null; // Avoid infinite loops
    seen.add(input);
  }

  // Handle arrays
  if (Array.isArray(input)) {
    return input
      .slice(0, limit)
      .map((item) => limitArrayElements(item, limit, seen));
  }

  // Handle objects
  if (typeof input === "object") {
    const result = {};
    for (const key in input) {
      if (Object.prototype.hasOwnProperty.call(input, key)) {
        result[key] = limitArrayElements(input[key], limit, seen);
      }
    }
    return result;
  }

  // Return primitive values unchanged
  return input;
}
export function buildSchemaFromApiSpec(apiDefs) {
  const top = {
    type: "object",
    additionalProperties: false,
    properties: {},
    required: [],
  };

  if (!Array.isArray(apiDefs)) return top;

  // ---------- Helpers ----------
  const toBool = (v) =>
    typeof v === "boolean" ? v : String(v).toLowerCase() === "true";

  const cleanType = (t) => {
    const m = String(t || "string").toLowerCase();
    return [
      "string",
      "number",
      "boolean",
      "array",
      "object",
      "integer",
    ].includes(m)
      ? m === "integer"
        ? "number"
        : m
      : "string";
  };

  const cleanDesc = (s) =>
    String(s || "")
      .replace(/^[\s*⭐⚪•\-–—\u2022]+/g, "")
      .replace(/\s+/g, " ")
      .trim();

  // include by default; exclude only when isDefault === true
  const includeField = (node) => !toBool(node?.isDefault);

  // Merge required names uniquely (never add when false)
  const pushReq = (arr, name, requiredFlag) => {
    if (requiredFlag && name && !arr.includes(name)) arr.push(name);
  };

  // Merge properties of two object schemas (union by name)
  const mergeObjectSchemas = (a, b) => {
    const out = {
      type: "object",
      additionalProperties: false,
      properties: { ...(a?.properties || {}) },
    };
    const req = new Set([...(a?.required || [])]);
    for (const [k, v] of Object.entries(b?.properties || {})) {
      if (!out.properties[k]) out.properties[k] = v;
    }
    for (const r of b?.required || []) req.add(r);
    if (req.size) out.required = Array.from(req);
    return out;
  };

  // STRICT collapse:
  // Only collapse if the item schema has EXACTLY ONE property total and that property is a primitive
  // (e.g., items: { searchType: "string" } -> items: { type: "string" }).
  const maybeCollapseArrayItemToPrimaryPrimitive = (itemSchema) => {
    if (!itemSchema || itemSchema.type !== "object") return itemSchema;
    const props = itemSchema.properties || {};
    const propNames = Object.keys(props);
    if (propNames.length !== 1) return itemSchema; // keep object if more than one prop (e.g., has pagination)

    const onlyKey = propNames[0];
    const sch = props[onlyKey];
    if (!sch || !sch.type || sch.type === "object" || sch.type === "array")
      return itemSchema;

    // If required exists and requires a different field, don't collapse
    const req = new Set(itemSchema.required || []);
    if (req.size && (!req.has(onlyKey) || req.size > 1)) return itemSchema;

    return { type: sch.type, description: cleanDesc(sch.description) };
  };

  // Forward declaration for mutual recursion
  function fieldToSchema(node) {
    // Skip if isDefault true
    if (!includeField(node)) return { schema: null, required: false };

    const outerType = cleanType(node?.type);
    const requiredFlag = toBool(node?.required);
    const description = cleanDesc(node?.description);
    const val = node?.VALUE;

    // 1) If VALUE exists, it drives the schema.
    if (val !== undefined) {
      // Special case: object wrapper with a single primitive VALUE leaf → promote
      if (
        outerType === "object" &&
        val &&
        typeof val === "object" &&
        val.type &&
        Array.isArray(val.data) &&
        val.data.length === 1 &&
        cleanType(val.type) !== "object" &&
        cleanType(val.type) !== "array"
      ) {
        const leaf = val.data[0];
        if (!includeField(leaf)) return { schema: null, required: false };
        return {
          schema: {
            type: cleanType(leaf?.type || val?.type),
            description: cleanDesc(node?.description || leaf?.description),
          },
          required: requiredFlag || toBool(leaf?.required),
        };
      }

      // General VALUE handling (supports object/array/primitive and example arrays)
      const schemaFromValue = valueToSchema(val, description);
      return { schema: schemaFromValue, required: requiredFlag };
    }

    // 2) No VALUE: fall back to node.type and optional node.data for structure.
    if (outerType === "object") {
      const obj = {
        type: "object",
        additionalProperties: false,
        properties: {},
        description,
      };
      if (Array.isArray(node?.data)) {
        const req = [];
        for (const child of node.data) {
          if (!includeField(child)) continue;
          const { schema, required } = fieldToSchema(child);
          if (!child?.name || !schema) continue;
          obj.properties[child.name] = schema;
          pushReq(req, child.name, required || toBool(child?.required));
        }
        if (req.length) obj.required = req;
      }
      return { schema: obj, required: requiredFlag };
    }

    if (outerType === "array") {
      // Bare array without VALUE → default to array of strings
      return {
        schema: { type: "array", items: { type: "string" }, description },
        required: requiredFlag,
      };
    }

    // Primitive fallback
    return { schema: { type: outerType, description }, required: requiredFlag };
  }

  // Build schema from a VALUE object (VALUE: { type, data }) or VALUE example (array/primitive)
  function valueToSchema(val, inheritedDesc = "") {
    if (val == null) return null;

    // VALUE given as a raw array of node-objects (e.g., searchTypes)
    if (Array.isArray(val)) {
      const nodeLike = val.some(
        (el) => el && typeof el === "object" && (el.type || el.data)
      );

      if (nodeLike) {
        let mergedItemSchema = null;

        for (const elementNode of val) {
          if (!elementNode || !includeField(elementNode)) continue;

          // Treat each array element like a node representing one item.
          const { schema } = fieldToSchema(elementNode);
          if (!schema) continue;

          if (!mergedItemSchema) {
            mergedItemSchema = schema;
          } else if (
            mergedItemSchema.type === "object" &&
            schema.type === "object"
          ) {
            mergedItemSchema = mergeObjectSchemas(mergedItemSchema, schema);
          } else if (schema.type === "object") {
            mergedItemSchema = schema; // prefer richer object
          }
        }

        if (!mergedItemSchema) {
          mergedItemSchema = {
            type: "object",
            additionalProperties: false,
            properties: {},
          };
        }

        // STRICT collapse (only when exactly one primitive property exists)
        mergedItemSchema =
          maybeCollapseArrayItemToPrimaryPrimitive(mergedItemSchema);

        return {
          type: "array",
          items: mergedItemSchema,
          description: cleanDesc(inheritedDesc),
        };
      }

      // Otherwise: infer items from first non-null element (primitive/array/object literal examples)
      let itemSchema;
      const first = val.find((x) => x !== undefined && x !== null);
      if (first == null) {
        itemSchema = { type: "string" };
      } else if (Array.isArray(first)) {
        itemSchema = { type: "array", items: { type: "string" } };
      } else if (typeof first === "object") {
        itemSchema = { type: "object", additionalProperties: true };
      } else {
        const t = cleanType(typeof first);
        itemSchema = { type: t === "integer" ? "number" : t };
      }
      return {
        type: "array",
        items: itemSchema,
        description: cleanDesc(inheritedDesc),
      };
    }

    // VALUE object with explicit type/data
    if (typeof val === "object" && val.type) {
      const vType = cleanType(val.type);

      if (vType === "object") {
        const obj = {
          type: "object",
          additionalProperties: false,
          properties: {},
          description: cleanDesc(inheritedDesc),
        };
        const children = Array.isArray(val.data) ? val.data : [];
        for (const child of children) {
          if (!includeField(child)) continue;
          const { schema, required } = fieldToSchema(child);
          if (!child?.name || !schema) continue;
          obj.properties[child.name] = schema;
          pushReq(
            obj.required || (obj.required = []),
            child.name,
            required || toBool(child?.required)
          );
        }
        if (obj.required && !obj.required.length) delete obj.required;
        return obj;
      }

      if (vType === "array") {
        const itemsArray = Array.isArray(val.data) ? val.data : [];
        let mergedItemSchema = null;

        for (const elementNode of itemsArray) {
          if (!includeField(elementNode)) continue;
          const { schema } = fieldToSchema(elementNode);
          if (!schema) continue;

          if (!mergedItemSchema) {
            mergedItemSchema = schema;
          } else if (
            mergedItemSchema.type === "object" &&
            schema.type === "object"
          ) {
            mergedItemSchema = mergeObjectSchemas(mergedItemSchema, schema);
          } else if (schema.type === "object") {
            mergedItemSchema = schema;
          }
        }

        if (!mergedItemSchema) {
          const first = itemsArray.find((x) => x != null);
          if (first) {
            const firstVal = first?.VALUE;
            const t = cleanType(firstVal?.type || first?.type || "string");
            mergedItemSchema =
              t === "object"
                ? {
                    type: "object",
                    additionalProperties: false,
                    properties: {},
                  }
                : { type: t };
          } else {
            mergedItemSchema = { type: "string" };
          }
        }

        // STRICT collapse
        mergedItemSchema =
          maybeCollapseArrayItemToPrimaryPrimitive(mergedItemSchema);

        return {
          type: "array",
          items: mergedItemSchema,
          description: cleanDesc(inheritedDesc),
        };
      }

      // Primitive VALUE container
      return { type: vType, description: cleanDesc(inheritedDesc) };
    }

    // Primitive example VALUE
    const primType = cleanType(typeof val);
    return { type: primType, description: cleanDesc(inheritedDesc) };
  }

  // ---------- Build top-level from first API def (original behavior) ----------
  for (const api of apiDefs) {
    // HEADERS
    for (const h of api.headers || []) {
      if (!includeField(h)) continue;
      const name = h?.name;
      if (!name) continue;
      const { schema, required } = fieldToSchema(h);
      if (!schema) continue;
      top.properties[name] = schema;
      pushReq(top.required, name, required || toBool(h?.required));
    }

    // PARAMS
    for (const p of api.params || []) {
      if (!includeField(p)) continue;
      const name = p?.name;
      if (!name) continue;
      const { schema, required } = fieldToSchema(p);
      if (!schema) continue;
      top.properties[name] = schema;
      pushReq(top.required, name, required || toBool(p?.required));
    }

    // PAYLOAD
    for (const pl of api.payload || []) {
      if (!includeField(pl)) continue;
      const name = pl?.name;
      if (!name) continue;
      const { schema, required } = fieldToSchema(pl);
      if (!schema) continue;
      top.properties[name] = schema;
      pushReq(top.required, name, required || toBool(pl?.required));
    }

    break; // first endpoint only
  }

  if (!Object.keys(top.properties).length) top.properties = {};
  if (!top.required.length) delete top.required;

  return top;
}

const IntegrationEditorConfig = ({
  initialJsons,
  setFormData,
  formData,
  field,
  setListData,
}) => {
  // console.log("formDataformDataformData", formData);
  const [alertDetails, setAlertDetails] = useState({
    status: false,
    severity: null,
    title: null,
    message: null,
  });
  const [input, setInput] = useState("");
  const [quick_replies, setquick_replies] = useState(null);
  let testingjson = {
    answer: {
      additionalProperties: false,
      description:
        "Schema for ticket booking confirmation, which requires at least one of the city, person_name, places and mandatory fields start_date, end_date, concert_name",
      properties: {
        keyword: {
          description: "The city where the concert is taking place",
          type: "string",
        },
      },
      required: ["start_date", "end_date", "concert_name"],
      type: "object",
    },
  };
  const [data, setData] = useState(testingjson);
  const [editingKeys, setEditingKeys] = useState({});
  const [editedKeys, setEditedKeys] = useState({});
  const [apiData, setApiData] = useState({});
  const [final_resulted_json, setFinal_resulted_json] = useState({});
  const [initialJson, setInitialJson] = useState(initialJsons);
  const [initialJson2, setInitialJson2] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [isLoading, setisLoading] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const nonEditableKeys = ["properties", "createdAt"];
  const nonDeletableKeys = ["properties", "user"];
  const nonDeletableObjects = ["answer", "config", "properties"];
  const hiddenKeys = ["secret", "debug", "required", "additionalProperties"];
  const options = uiExamples?.map((item) => item.uiName);
  const [selected_ui_custom_data, setSelected_ui_custom_data] = useState(null);
  const [selected_ui_name, setselected_ui_name] = useState(null);
  const [select_api_type, setSelectApiType] = useState("");
  const [userApiSchema, setuserApiSchema] = useState("");
  const [api_config, setApi_config] = useState(null);
  const [ui_primary_path, setui_primary_path] = useState("");
  const [uiDetails, setUiDetails] = useState("");
  const [mapUILoader, setmapUILoader] = useState(false);
  let [sample_template, setsample_template] = useState("");
  const [configType, setConfigType] = useState("apiconfig"); // New state for configuration type


    const [isRebuildConfirmOpen, setIsRebuildConfirmOpen] = useState(false);

  const openRebuildConfirm = () => setIsRebuildConfirmOpen(true);
  const closeRebuildConfirm = () => setIsRebuildConfirmOpen(false);

  const confirmRebuild = () => {
    console.log("Rebuild confirmed! Proceed with wiping API configurations...");
    setIsRebuildConfirmOpen(false);
    startBuilding()
  };

  const isNonEditableKey = (key) => nonEditableKeys.includes(key);
  const isNonDeletableKey = (key) => nonDeletableKeys.includes(key);
  const isNonDeletableObject = (key) => nonDeletableObjects.includes(key);
  const shouldHideRecursively = (key) => hiddenKeys.includes(key);
  const [customApiSchema, setcustomApiSchema] = useState(null);
  const [mcpConfig, setmcpConfig] = useState(null);

  // testing111111111
  const [isDialogOpen, setIsDialogOpen] = useState(false); // tracks popup visibility
  const [thinkingStatus, setthinkingStatus] = useState(false); // tracks popup visibility
  const [chosenApis, setChosenApis] = useState([]); // stores selected APIs
  const [userSchemaGeneratedAPis, setuserSchemaGeneratedAPis] = useState([]); // stores selected APIs

  // open dialog
  const handleDialogOpen = () => setIsDialogOpen(true);

  // close dialog
  const handleDialogClose = () => setIsDialogOpen(false);

  // when ApiSelector submits data
  const handleApiSelectionSubmit = async (selectedApis) => {
    console.log("🚀 ~ handleApiSelectionSubmit ~ selectedApis:", selectedApis);
    setChosenApis(selectedApis);

    try {
      handleSaveAlljson({ api_schema: selectedApis });
      let updatedSchema = buildSchemaFromApiSpec(selectedApis);
      console.log("updatedSchemaupdatedSchemaupdatedSchema", updatedSchema);
      let inputSchemaApi = {
        data: selectedApis,
      };
      let resultedData = convertSchema1ToSchema2All(inputSchemaApi);
      console.log(
        "updatedSchemaupdatedSchemaupdatedSchema resultedData",
        resultedData
      );
      setApi_config(resultedData[0]);
      let newData = {
        answer: updatedSchema,
      };
      setData(newData);

      let alertDetailss = {};
      alertDetailss.message = "Your input setup is created";
      alertDetailss.severity = "success";
      alertDetailss.status = true;
      alertDetailss.title = "Input parameters";
      setAlertDetails(alertDetailss);
    } catch (error) {
      setisLoading(false);
      let alertDetailss = {};
      alertDetailss.message =
        "Your input setup is getting errors. Please try again";
      alertDetailss.severity = "error";
      alertDetailss.status = true;
      alertDetailss.title = "Input parameters";
      setAlertDetails(alertDetailss);
    }

    handleDialogClose(); // close popup after submit
  };
  // testing111111111

  const handleSaveAlljson = (json) => {
    // console.log("🚀 ~ handleSaveAlljson ~ json:", json);
    if (json?.response_object) {
      setUiDetails(json?.response_object);
    }
    setFormData((prev) => {
      let result_data = { ...prev, data: { ...prev?.data, ...json } };
      return result_data;
    });
  };

  useEffect(() => {
    if (Object.entries(formData?.data || {}).length) {
      let description = formData?.data?.tool_description;
      setFinal_resulted_json((prev) => {
        let finalDatas = { ...prev, ...formData?.data };
        return finalDatas;
      });
      setSelected_ui_custom_data(formData?.data?.response_object?.parameters);
      if (formData?.data?.response_object) {
        setUiDetails(formData?.data?.response_object);
      }
      setselected_ui_name(formData?.data?.response_object?.display_type);
      setui_primary_path(formData?.data?.response_object?.primary_path);
      setApi_config(formData?.data?.action_type);
      setConfigType(formData?.data?.config_type||"apiconfig");
      setmcpConfig(formData?.data?.mcpConfig);
      setuserSchemaGeneratedAPis(formData?.data?.api_schema);
      setuserApiSchema(formData?.data?.api_schema_prompt);
      setSelectApiType(formData?.data?.action_type?.type);
      setquick_replies(formData?.data?.quick_replies);
      setthinkingStatus(formData?.data?.isThinking);
      if (description) {
        setInput(description);
      }
      setsample_template(null);
      setData(formData?.data?.tool_parameters);
      if (formData?.data?.apiResponse) {
        setApiData(formData?.data?.apiResponse);
      }
    }
  }, []);

  useEffect(() => {
    if (
      formData?.data?.sample_template &&
      formData?.data?.sample_template != sample_template
    ) {
      setsample_template(formData?.data?.sample_template);
      let allTemplates = field?.sample_templates || [];
      let result_obj = {};
      let description = "";
      let apiData = {};
      let llm_function_parameter = { answer: {} };
      allTemplates.filter((template) => {
        if (template?.template_name === formData?.data?.sample_template) {
          description =
            formData?.data?.tool_description || template?.tool_description;
          apiData = template?.action_type;
          llm_function_parameter = template?.tool_parameters;
          return null;
        }
      });
      apiData = formData?.data?.action_type?.type
        ? formData?.data?.action_type
        : apiData;
      setSelectApiType(apiData?.type);
      setApi_config(apiData);
      setInput(description);
      handleSaveAlljson({
        tool_description: description,
        action_type: apiData,
      });
      setData(llm_function_parameter);
    }
  }, [formData?.data?.sample_template]);

  const fetchData = async () => {
    try {
      setisLoading(true);
      const response = await fetch("/api/createInputParams", {
        method: "post",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ integration_description: input }),
      });
      const recievedData = await response.json();
      let status = recievedData?.data?.status || false;
      let inputParameters = recievedData?.data?.data || null;
      if (!status && !inputParameters) {
        throw new Error("Error in making the input Parameters");
      }
      if (!inputParameters?.answer) {
        throw new Error("Error in making the input Parameters");
      }
      setData(inputParameters || {});
      handleSaveAlljson({ tool_parameters: recievedData?.data?.data || {} });
      setisLoading(false);
      let alertDetailss = {};
      alertDetailss.message = "Your input setup is created";
      alertDetailss.severity = "success";
      alertDetailss.status = true;
      alertDetailss.title = "Input parameters";
      setAlertDetails(alertDetailss);
    } catch (error) {
      setisLoading(false);
      let alertDetailss = {};
      alertDetailss.message =
        "Your input setup is getting errors. Please try again";
      alertDetailss.severity = "error";
      alertDetailss.status = true;
      alertDetailss.title = "Input parameters";
      setAlertDetails(alertDetailss);
    }
  };
  const startBuilding = async () => {
    try {
      setisLoading(true);
      const response = await fetch("/api/create_api_rules", {
        method: "post",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ integration_description: userApiSchema }),
      });
      const recievedData = await response.json();
      console.log("🚀 ~ startBuilding ~ recievedData:", recievedData);
      let status = recievedData?.data?.status || false;
      let inputParameters = recievedData?.data?.data || null;
      if (!status && !inputParameters) {
        throw new Error("Error in making the input Parameters");
      }
      handleDialogOpen();

      setuserSchemaGeneratedAPis(inputParameters);

      let alertDetailss = {};
      alertDetailss.message = "Your input setup is created";
      alertDetailss.severity = "success";
      alertDetailss.status = true;
      alertDetailss.title = "Input parameters";
      setAlertDetails(alertDetailss);
      setisLoading(false);
    } catch (error) {
      setisLoading(false);
      let alertDetailss = {};
      alertDetailss.message =
        "Your input setup is getting errors. Please try again";
      alertDetailss.severity = "error";
      alertDetailss.status = true;
      alertDetailss.title = "Input parameters";
      setAlertDetails(alertDetailss);
    }
  };

  const handleChange = (path, newValue) => {
    const newData = { ...data };
    let temp = newData;
    for (let i = 0; i < path.length - 1; i++) temp = temp[path[i]];
    temp[path[path.length - 1]] = newValue;
    setData(newData);
  };

  const handleDelete = (path) => {
    const key = path[path.length - 1];
    if (isNonDeletableKey(key)) return;
    const newData = { ...data };
    let temp = newData;
    for (let i = 0; i < path.length - 1; i++) temp = temp[path[i]];
    delete temp[key];
    setData(newData);
  };

  const handleRename = (path, newKey) => {
    if (!newKey || path[path.length - 1] === newKey) return;
    const key = path[path.length - 1];
    if (isNonEditableKey(key)) return;
    const newData = { ...data };
    let temp = newData;
    for (let i = 0; i < path.length - 1; i++) temp = temp[path[i]];
    temp[newKey] = temp[key];
    delete temp[key];
    setData(newData);
    const keyPath = path.join(".");
    setEditingKeys((prev) => {
      const updated = { ...prev };
      delete updated[keyPath];
      return updated;
    });
  };

  const handleApiTypeChange = (event) => {
    setSelectApiType(event.target.value);
  };

  const styles = {
    container: {
      fontFamily: "Arial, sans-serif",
      // padding: "20px",
      margin: "10px auto",
      backgroundColor: "#202345",
      borderRadius: "8px",
      color: "white",
    },
    label: {
      display: "block",
      marginBottom: "8px",
      fontSize: "16px",
      fontWeight: "bold",
      marginBlock:20
    },
    select: {
      width: "100%",
      padding: "8px",
      fontSize: "16px",
      borderRadius: "4px",
      border: "1px solid #ccc",
    },
    result: {
      marginTop: "15px",
      color: "#333",
      fontSize: "15px",
    },
    button: {
      marginTop: "15px",
      padding: "8px 12px",
      fontSize: "14px",
      backgroundColor: "#007bff",
      color: "#fff",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
    },
  };

  function getValueFromPath(path, obj) {
    const parts = path.replace(/\[(\d+)\]/g, ".$1").split(".");
    return parts.reduce((acc, part) => acc && acc[part], obj);
  }

  const handleMapToUI = async () => {
    try {
      setmapUILoader(true);
      let selected_ui = uiExamples[0]?.data?.blocks;
      let uialignment = selectedComponent?.data?.blocks;
      if (selected_ui && selected_ui?.length) {
        let allTypes = selected_ui?.map((item) => item.type);
        let schema = `{
            "display_name": "<Label for UI>",
            "path": "<dot.notation.path.to.field>",
            "display_type": "<type like ${allTypes.join(" , ")} etc>",
            "value": "<value for the field>"
            },
            ...
            `;
        let ui_data = limitArrayElements(apiData);
        const res = await fetch("/api/mapResponseToUI", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ui_input: ui_data,
            schema: schema,
            uialignment,
          }),
        });
        const ai_data = await res.json();
        let aiUIMapped = ai_data?.data || [];
        if (!aiUIMapped?.length) {
          throw new Error("UI is not mapped");
        }
        setSelected_ui_custom_data(aiUIMapped);
        handleSaveAlljson({
          response_object: {
            parameters: aiUIMapped,
            primary_path: ai_data?.primary_path,
            display_type: selectedComponent?.uiName,
          },
        });
        setselected_ui_name(selectedComponent?.uiName);
        setInitialJson2(ai_data?.remaining_data || []);
        let primary_path = ai_data?.primary_path;
        let newPrimaryPath = "";
        if (primary_path) {
          if (primary_path?.startsWith("main.data")) {
            newPrimaryPath = primary_path;
          } else {
            newPrimaryPath = "main.data." + primary_path;
            let values = getValueFromPath(newPrimaryPath, apiData);
            if (!values) {
              newPrimaryPath = primary_path;
            }
          }
        }
        setui_primary_path(newPrimaryPath || "");
        setmapUILoader(false);
        let alertDetailss = {};
        alertDetailss.message = "Your choosed UI is successfully mapped";
        alertDetailss.severity = "success";
        alertDetailss.status = true;
        alertDetailss.title = "UI Look";
        setAlertDetails(alertDetailss);
      }
    } catch (error) {
      setmapUILoader(false);
      let alertDetailss = {};
      alertDetailss.message = "Error in mapping the UI. Please Try Again";
      alertDetailss.severity = "error";
      alertDetailss.status = true;
      alertDetailss.title = "UI Look";
      setAlertDetails(alertDetailss);
    }
  };

  const apiOptions = [
    {
      id: "result_api_params",
      label:
        "Add your Input Params to the URL of Your API Request (Parameters)",
    },
    {
      id: "result_api_payload",
      label: "Add your Input Params to the Body of Your API Request (Payload)",
    },
  ];

  const components = uiExamples.map((item, index) => ({
    key: item?.uiName + "comp" + index,
    label: item?.uiName,
    component:
      item.uiName === "Itinerary" ? (
        <ItineraryWidget blocks={item.data?.blocks} />
      ) : item.uiName === "Textual_format_list" ? (
        <BulletList />
      ) : (
        <CardPreview blocks={item.data?.blocks} key={index} />
      ),
    comp_data: item,
  }));

  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => setOpenDialog(false);

  const handleSelectComponent = (component) => {
    setselected_ui_name(component?.uiName);
    setSelectedComponent(component);
  };

  const re_map_ui = async (apiData) => {
    try {
      // console.log("uiDetailsuiDetails", uiDetails);
      if (uiDetails && uiDetails?.parameters?.length) {
        const response = await fetch("/api/re_map_ui", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            apiData: apiData,
            selectedUi: uiDetails,
          }),
        });
        const ai_data = await response.json();
        let aiUIMapped = ai_data?.data || [];
        if (!aiUIMapped?.length) {
          throw new Error("UI is not mapped");
        }
        setSelected_ui_custom_data(aiUIMapped?.[0]);
      }
    } catch (error) {}
  };

  const handleConfigTypeChange = (event) => {
    setConfigType(event.target.value);
    handleSaveAlljson({ config_type: event.target.value });
  };

  const steps = [
    {
      title: "Get Started (*)",
      description:
        "Enter your platform details, login info, and connection settings to start using your function.",
      icon: <Info />,
      loadingMessages: [
        "Starting up...",
        "Checking your parameters...",
        "Making connections...",
      ],
      isSkip: false,
      // isSkip: true,
      component: (
        <>

        <div style={{
                display:"flex", alignItems:"center",gap:10
              }}>

        <p style={styles.label}>Integration instructions</p>  
        <Tooltip
  placement="top"
  title={
    <>
      <b>Integration Instructions:</b> Provide clear steps or guidance on how to integrate your system with this configuration. <br /><br />

      🔹 It also helps the chatbot choose the correct integration when multiple integrations are available or configured in agent. <br /><br />
      🔹 This guidance will help the LLM understand how to interact with your API or system effectively. <br />

      💡 Tip: Be concise and clear — the better the instructions, the more reliably the model can follow them.
    </>
  }
>
  <InfoIcon />
</Tooltip>

              </div>

          <textarea
            rows="4"
            placeholder="Type something..."
            value={input}
            onChange={(e) => {
              e.preventDefault();
              setInput(e.target.value);
              handleSaveAlljson({ tool_description: e.target.value });
            }}
            style={{
              padding: "8px",
              width: "100%",
              borderRadius: "4px",
              background: "transparent",
              border: "1px solid rgb(102, 102, 102)",
              color: "white",
              resize: "vertical",
            }}
          />

          <div >
 <div style={{
                display:"flex", alignItems:"center",gap:10
              }}>
            <p style={styles.label}>Configuration Type</p>  
           <Tooltip
  placement="top"
  title={
    <>
      <b>Configuration Type:</b> Choose how you want to interact with the system. <br /><br />

      <b>🔹 API Config:</b> Connect directly via the API. 
      Allows programmatic integration for sending and receiving data reliably. <br /><br />

      <b>🔹 MCP (Model Context Protocol):</b> Leverage model-driven context management. 
      MCP helps manage state and context for more intelligent responses. <br /><br />

      <b>🔹 None:</b> Create your own custom configuration. 
      You can integrate your workflow manually, either with API calls or without using the API at all. <br /><br />

      💡 Tip: Choose the option that best suits your needs — API for direct integration, MCP for context-aware interactions, or None to build your own setup.
    </>
  }
>
  <InfoIcon />
</Tooltip>

              </div>
            <select
              value={configType || ""}
              onChange={handleConfigTypeChange}
              style={styles.select}
            >
              <option value="" disabled>
                Select Configuration Type
              </option>
              <option value="">NONE</option>
              <option value="mcp">MCP</option>
              <option value="apiconfig">API Config</option>
            </select>

            {configType === "apiconfig" && (
              <>
              <div style={{
                display:"flex", alignItems:"center",marginTop:15,gap:10
              }}>

              <p style={{
      display: "block",
    
      fontSize: "16px",
      fontWeight: "bold",
      margin:"0px",
      marginBottom:0
    }}>Api Schema information</p> 
    
    <Tooltip placement="top" title={    <>
      This API schema is required to establish proper integration and ensure consistent communication with the API <br /><br />

      <b>🔹 Headers Example:</b><br />
      Authorization: Bearer YOUR_TOKEN <br />
      Content-Type: application/json <br /><br />

      <b>🔹 Params Example:</b><br />
      GET /textsearch/json?query=pizza+near+New+York&key=YOUR_API_KEY <br /><br />

      <b>🔹 Payload Example (POST APIs):</b><br />
      {"{"} "location": "40.7128,-74.0060", "radius": 1500 {"}"}
    </>}>
                                        <InfoIcon />
                                      </Tooltip>
              </div>
                <textarea
                  rows="6"
                  placeholder={`Please follow this guide when filling in your API details or OpenAPI schema. 
It will help ensure your request is understood correctly.

🔹 Headers (who you are and how the request should be handled):
Authorization: Bearer ...

🔹 Params (what you are searching for or requesting):
GET /textsearch/json?query=...&key=...

🔹 Payload (extra details you want to send, mostly for POST requests):
{
  'location': 'latitude,longitude',
  'radius': ...
}

💡 Tip: Replace '...' with your actual values.`}
                  value={userApiSchema}
                  onChange={(e) => {
                    e.preventDefault();
                    let value = e.target.value;
                    setuserApiSchema(e.target.value);
                    handleSaveAlljson({ api_schema_prompt: value });
                  }}
                  style={{
                    padding: "8px",
                    width: "100%",
                    borderRadius: "4px",
                    background: "transparent",
                    border: "1px solid rgb(102, 102, 102)",
                    color: "white",
                    resize: "vertical",
                    marginBlock: 10,
                  }}
                />
                {userSchemaGeneratedAPis && (
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      marginBottom: "10px",
                    }}
                  >
                    <SuperAppButton onClick={() => handleDialogOpen()}>
                      Add more Fields
                    </SuperAppButton>
                  </div>
                )}
                <div
                  style={{ display: "flex", gap: "10px", marginBottom: "10px" }}
                >
                  <SuperAppButton
                    onClick={()=>{
                      if(userSchemaGeneratedAPis){

                        openRebuildConfirm()
                      }else{
                        confirmRebuild()
                      }
                    
                    }}
                    showSpinner={true}
                    loading={isLoading}
                  >
                    {/* {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Start Building"
                )} */}
                   {userSchemaGeneratedAPis? "Build Again":"Start Building"} 
                  </SuperAppButton>
                </div>


                <>

    

      {<Dialog open={isRebuildConfirmOpen} onClose={closeRebuildConfirm} >
        <DialogTitle sx={{ background: "#1A1C37",color:"white" }}>Rebuild Confirmation</DialogTitle>
        <DialogContent sx={{ background: "#1A1C37",color:"white"  }}>
          <Typography>
            Are you sure you want to <strong>re-build</strong> ? This will
            <strong> wipe out existing API configurations</strong>.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ background: "#1A1C37",color:"white"  }}>
          <SuperAppButton onClick={closeRebuildConfirm} style={{background:"#9C001B",border:'none'}}>
            Cancel
          </SuperAppButton>
          <SuperAppButton onClick={confirmRebuild} >
            Confirm Rebuild
          </SuperAppButton>
        </DialogActions>
      </Dialog>}
    </>
              </>
            )}
          </div>
          {(configType == "" || configType == "mcp") && (
            <div style={{ display: "flex", gap: "10px", marginBlock: "10px" }}>
              <SuperAppButton
                onClick={fetchData}
                showSpinner={true}
                loading={isLoading}
              >
                {/* {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                )} */}
                Create Input Parameters
              </SuperAppButton>
            </div>
          )}
          {/* Popup dialog */}
          <Dialog
            open={isDialogOpen}
            onClose={handleDialogClose}
            maxWidth="md"
            fullWidth
            sx={{ background: "#1A1C37" }}
          >
            <DialogTitle sx={{ background: "#1A1C37", color: "white" }}>
              Select APIs
            </DialogTitle>
            <DialogContent dividers sx={{ background: "#1A1C37" }}>
              <ApiSelector
                apiData={userSchemaGeneratedAPis}
                onSubmit={handleApiSelectionSubmit}
              />
            </DialogContent>
            <DialogActions sx={{ background: "#1A1C37" }}>
              <Button onClick={handleDialogClose} color="secondary">
                Cancel
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ),
      isDisabled: data ? true : false,
    },
    {
      title: "Set Up Inputs (*)",
      description:
        "Choose the inputs and data format needed for your integration and to prepare for the next API call.",
      icon: <Settings />,
      loadingMessages: [
        "Loading input options...",
        "Checking your choices...",
        "Saving inputs...",
        "Inputs ready...",
      ],
      isSkip: false,
      // isSkip: true,
      component: (
        <>
          {data && (
            <DynamicJsonEditorV1
              default_json={data}
              default_options={field?.default_options}
              default_path={field?.default_path}
              setListData={(data) => {
                if (data) {
                  setData(data);
                  handleSaveAlljson({ tool_parameters: data });
                }
              }}
              setDeletedData={(data) => {
                if (data) {
                
                  handleSaveAlljson({ deletedToolParameters: data });
                }
              }}
              customTooltip={field?.customTooltip}
              formData={formData?.data}
              fieldData={{ ...field, name: "tool_parameters" }}
              sample_templates={field?.sample_templates}
            />
          )}
        </>
      ),
      isDisabled: data ? true : false,
    },

    {
      title: "Configure Integration",
      description:
        "Set up your integration based on the chosen configuration type.",
      icon: <Api />,
      loadingMessages: [
        "Setting up integration...",
        "Validating configuration...",
        "Saving settings...",
        "Integration ready...",
      ],
      isSkip: true,
      component: (
        <>
          {configType === "mcp" ? (
            <MCPWorkflow
              handleSaveAlljson={(data)=>{
                console.log("🚀 ~ MCPWorkflowdata:", data)
                setApiData(data?.apiResponse)
                handleSaveAlljson(data)
              }}
              final_resulted_json={formData?.data}
              ll_data={data}
              setAllMcp ={(data)=>{
                handleSaveAlljson({mcpConfig:data})
              }}
              previousNodes={mcpConfig}
              thinkingStatus={thinkingStatus}
            />
          ) : configType === "apiconfig" ? (
            <APICallUI
              ll_data={data}
              select_api_type={select_api_type}
              setApiData={setApiData}
              settingApiData={setApiData}
              apiOptions={apiOptions}
              handleApiTypeChange={handleApiTypeChange}
              api_config={api_config}
              final_resulted_json={formData?.data}
              handleSaveAlljson={(apiDetails) => {
                if (apiDetails) {
                  if (apiDetails?.action_type) {
                    setApi_config({
                      ...api_config,
                      ...apiDetails?.action_type,
                    });
                  }
                  handleSaveAlljson(apiDetails);
                }
              }}
              re_map_ui={re_map_ui}
              isOpen={true}
              setAlertDetails={setAlertDetails}
            />
          ) : (
            <Typography>
              Please select a configuration type in the previous step.
            </Typography>
          )}
        </>
      ),
      isDisabled: Object.entries(apiData).length?true:false,
    },
    {
      title: "Pick Your Look",
      description:
        "Choose a layout, theme, or style for your interface after the API works, or skip for text results.",
      icon: <Palette />,
      loadingMessages: [
        "Loading design options...",
        "Applying your style...",
        "Saving your choices...",
        "Design complete...",
      ],
      isSkip: true,
      component: (
        <div>
          <div style={{ overflow: "auto", height: "600px" }}>
            <ComponentSelectorDialog
              components={components}
              open={openDialog}
              onClose={handleCloseDialog}
              onSelect={handleSelectComponent}
              isDailog={false}
              width={"400px"}
              selected_ui_name={selected_ui_name}
            />
          </div>
          <div style={{ marginTop: 10 }}>
            <SuperAppButton
              onClick={handleMapToUI}
              disabled={selectedComponent ? false : true}
            >
              {mapUILoader ? (
                <CircularProgress
                  sx={{ color: "white" }}
                  style={{ height: "20px", width: "20px" }}
                />
              ) : (
                "Map to UI"
              )}
            </SuperAppButton>
          </div>
        </div>
      ),
      isDisabled: true,
    },
    {
      title: "Build Widget",
      description:
        "Review and edit your widget’s final look to make sure it fits perfectly in your app.",
      icon: <Widgets />,
      loadingMessages: [
        "Creating widget...",
        "Adding final touches...",
        "Previewing widget...",
        "Widget ready...",
      ],
      isSkip: true,
      component: (
        <>
          {selected_ui_custom_data ? (
            <>
              {selected_ui_name === "Itinerary" ? (
                <ItineraryWidget blocks={selected_ui_custom_data} />
              ) : selected_ui_name === "Textual_format_list" ? (
                <BulletList />
              ) : (
                <>
                  <CustomEditor
                    initialJsons={selected_ui_custom_data}
                    handleSaveAllJson={handleSaveAlljson}
                    primary_path={ui_primary_path}
                    ui_name={selected_ui_name}
                    apiData={apiData}
                    final_resulted_json={formData}
                  />
                </>
              )}
            </>
          ) : null}
        </>
      ),
      isDisabled: true,
    },
  ];

  const [activeStep, setActiveStep] = useState(0);
  const [lastStep, setLastStep] = useState(null);
  const [isStepLoading, setisStepLoading] = useState(false);

  const handleNext = () => {
    setisStepLoading(true);
    setTimeout(() => {
      if (activeStep < steps.length - 1) {
        setLastStep(steps[activeStep].title);
      }
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      setisStepLoading(false);
    }, 3000);
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
      setLastStep(activeStep > 1 ? steps[activeStep - 2].title : null);
    }
  };

  const handleSkip = () => {
    if (activeStep < steps.length - 1) {
      setLastStep(steps[activeStep].title);
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const isLastStep = activeStep === steps.length - 1;
  const [activeTab, setActiveTab] = useState(0);

  return (
    <>
      <ErrorBoundary>
        {!formData?.data?.id ? (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  width: "100%",
                  backgroundColor: "#1A1C37",
                  padding: "40px",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    borderBottom: "1px solid #374151",
                    paddingBottom: "16px",
                    marginBottom: "32px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <h1
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "600",
                      color: "#E0E7FF",
                      margin: 0,
                    }}
                  >
                    Configuration Wizard
                  </h1>
                </div>
                <Stepper
                  activeStep={activeStep}
                  alternativeLabel
                  sx={{
                    marginBottom: "24px",
                    ".Mui-active": {
                      color: "#7C3AED !important",
                      fontWeight: "bold !important",
                    },
                  }}
                  connector={<ColorlibConnector />}
                >
                  {steps.map((step, index) => (
                    <Step key={index}>
                      <StepLabel
                        icon={step.icon}
                        sx={{
                          "& .MuiStepLabel-iconContainer": {
                            color: activeStep >= index ? "#7C3AED" : "#9CA3AF",
                            "& .MuiSvgIcon-root": {
                              fontSize: "1.5rem",
                            },
                          },
                          "& .MuiStepLabel-label": {
                            color: activeStep >= index ? "#ffff" : "#A3BFFA",
                            fontWeight: activeStep === index ? "bold" : "400",
                            fontSize: "1rem",
                          },
                        }}
                      >
                        {step.title}
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
                <AnimatePresence mode="wait">
                  {activeStep === steps.length ? (
                    <motion.div
                      key="completed"
                      style={{
                        padding: "32px",
                        textAlign: "center",
                        borderRadius: "8px",
                      }}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.5 }}
                    >
                      <Typography
                        variant="h5"
                        style={{
                          color: "#A3BFFA",
                        }}
                      >
                        All steps completed - you're finished!
                      </Typography>
                    </motion.div>
                  ) : (
                    <StepContent
                      key={activeStep}
                      title={steps[activeStep].title}
                      description={steps[activeStep].description}
                      component={steps[activeStep]?.component}
                      onNext={handleNext}
                      onBack={handleBack}
                      onSkip={handleSkip}
                      lastStep={lastStep}
                      isStepLoading={isStepLoading}
                      loadingMessages={steps[activeStep].loadingMessages}
                      activeStep={activeStep}
                      isLastStep={isLastStep}
                      isSkip={steps[activeStep].isSkip}
                      isDisabled={steps[activeStep]?.isDisabled}
                    />
                  )}
                </AnimatePresence>
                <div
                  style={{
                    marginTop: "32px",
                    textAlign: "center",
                    color: "#9CA3AF",
                    fontSize: "0.875rem",
                  }}
                >
                  Step {activeStep + 1} of {steps.length}
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            {steps.length > 1 && (
              <div className="superappobject">
                <div className="top-menu">
                  <ul className="menu-lists">
                    {steps.map((tab, index) => (
                      <li className="menu-item">
                        <button
                          type="button"
                          key={index}
                          onClick={() => setActiveTab(index)}
                          className={activeTab === index ? "active" : ""}
                        >
                          {tab.title}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
                {steps.map((tab, index) =>
                  activeTab === index ? (
                    <div style={{ marginTop: 10 }}>{tab?.component}</div>
                  ) : null
                )}
              </div>
            )}
          </>
        )}
        {alertDetails?.status && (
          <AlertMessage
            onClose={() =>
              setAlertDetails({
                status: false,
                severity: null,
                title: null,
                message: null,
              })
            }
            open={alertDetails?.status}
            message={alertDetails?.message}
            severity={alertDetails?.severity}
            title={alertDetails?.title}
          />
        )}
      </ErrorBoundary>
    </>
  );
};

export default IntegrationEditorConfig;


const ComponentSelectorDialog = ({
  components,
  open,
  onClose,
  onSelect,
  isDailog,
  width,
  selected_ui_name,
}) => {
  const [selectedComponent, setSelectedComponent] = useState(null);

  // Handle radio button change
  const handleSelectionChange = (event) => {
    const key = event.target.value;
    setSelectedComponent(key);

    // In non-dialog mode, immediately return the selected component
    if (!isDailog) {
      const selected = components.find((comp) => comp.key === key);
      onSelect(selected?.comp_data);
    }
  };

  // Handle dialog close and return the selected component
  const handleConfirm = () => {
    const selected = components.find((comp) => comp.key === selectedComponent);
    onSelect(selected?.comp_data);
    onClose && onClose();
  };

  useEffect(() => {
    if (selected_ui_name) {
      // Accept key or label; store key
      const selected =
        components.find((c) => c.key === selected_ui_name) ||
        components.find((c) => c.label === selected_ui_name);

      if (selected) {
        setSelectedComponent(selected.key);
        if (!isDailog) onSelect(selected.comp_data);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected_ui_name, components, isDailog]);

  // ----- Inline styles -----
  const styles = {
    overlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.35)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
    },
    dialog: {
      width: "90%",
      maxWidth: 900,
      background: "#fff",
      borderRadius: 10,
      overflow: "hidden",
      boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
      display: "flex",
      flexDirection: "column",
      maxHeight: "85vh",
    },
    header: {
      padding: "14px 18px",
      background: "#f5f6fa",
      fontWeight: 700,
      fontSize: 16,
    },
    content: {
      padding: 16,
      background: "#f5f6fa",
      overflow: "hidden",
    },
    actions: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 8,
      padding: 12,
      background: "#f5f6fa",
      borderTop: "1px solid #e5e7eb",
    },
    button: {
      padding: "8px 14px",
      borderRadius: 8,
      border: "1px solid #e5e7eb",
      background: "#fff",
      cursor: "pointer",
      fontWeight: 600,
    },
    buttonPrimary: {
      padding: "8px 14px",
      borderRadius: 8,
      border: "1px solid #1976d2",
      background: "#1976d2",
      color: "#fff",
      cursor: "pointer",
      fontWeight: 600,
    },
    scroller: {
      display: "flex",
      flexDirection: "row",
      gap: 10,
      overflowX: "auto",          // horizontal scroll
      paddingBottom: 8,
      scrollbarWidth: "thin",
      // Optional snap (uncomment to enable)
      // scrollSnapType: "x mandatory",
    },
    card: {
      width: width || "400px",
      minWidth: width || "400px", // keep width in horizontal row
      background: "#fff",
      border: "1px solid #e5e7eb",
      borderRadius: 10,
      padding: 12,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 10,
      cursor: "pointer",
      height:"fit-content"
      // flex: "0 0 auto",
      // scrollSnapAlign: "start", // enable if using snap
    },
    // Native radio with white bg and blue when checked
    radio: (checked) => ({
      accentColor: "#1976d2", // blue inner dot
      width: 18,
      height: 18,
      cursor: "pointer",
      backgroundColor: "#fff", // always white
      outline: "none",
      border: checked ? "2px solid #1976d2" : "2px solid #ccc",
      borderRadius: "50%",
      appearance: "none",
      WebkitAppearance: "none",
      position: "relative",
      display: "grid",
      placeContent: "center",
    }),
    innerDot: {
      position: "absolute",
      width: 8,
      height: 8,
      borderRadius: "50%",
      backgroundColor: "#1976d2",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      pointerEvents: "none",
    },
    labelText: {
      fontSize: 14,
      color: "#374151",
      fontWeight: 600,
      textAlign: "center",
    },
    topRadioRow: {
      display: "flex",
      justifyContent: "center",
      width: "100%",
      marginBottom: 4,
    },
    preview: { width: "100%", textAlign: "center" },
  };

  const Cards = (
    <div style={styles.scroller}>
      {components.map(({ key, label, component: Component }) => {
        const checked = selectedComponent === key;

        // Click whole card: select by key
        const clickSelect = () =>
          handleSelectionChange({ target: { value: key } });

        return (
          <div key={key} style={styles.card} onClick={clickSelect}>
            {/* Radio centered at top */}
            {/* Label under preview (show in both modes for clarity) */}
            <span style={styles.labelText}>{label}</span>
            <div style={styles.topRadioRow}>
              <div style={{ position: "relative" }}>
                <input
                  type="radio"
                  name="selector"
                  value={key}
                  checked={checked || false}
                  onChange={handleSelectionChange}
                  style={styles.radio(checked)}
                />
                {checked && <div style={styles.innerDot} />}
              </div>
            </div>

            {/* Preview block */}
            <div >
              {typeof Component === "function" ? <Component /> : Component}
            </div>

            
          </div>
        );
      })}
    </div>
  );

  if (isDailog) {
    // Dialog mode
    if (!open) return null;
    return (
      <div style={styles.overlay} role="dialog" aria-modal="true">
        <div style={styles.dialog}>
          <div style={styles.header}>Select a Design</div>
          <div style={styles.content}>{Cards}</div>
          <div style={styles.actions}>
            <button style={styles.button} onClick={onClose}>
              Cancel
            </button>
            <button
              style={styles.buttonPrimary}
              onClick={handleConfirm}
              disabled={!selectedComponent}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Non-dialog mode
  return <div style={{ width: "100%" }}>{Cards}</div>;
};



// const ComponentSelectorDialog = ({
//   components,
//   open,
//   onClose,
//   onSelect,
//   isDailog,
//   width,
//   selected_ui_name,
// }) => {
//   const [selectedComponent, setSelectedComponent] = useState(null);

//   // Handle radio button change
//   const handleSelectionChange = (event) => {
//     setSelectedComponent(event.target.value);
//     if (!isDailog) {
//       const selected = components.find(
//         (comp) => comp.key === event.target.value
//       );
//       onSelect(selected?.comp_data); // Return the selected component
//     }
//   };

//   // Handle dialog close and return the selected component
//   const handleConfirm = () => {
//     const selected = components.find((comp) => comp.key === selectedComponent);
//     onSelect(selected?.comp_data); // Return the selected component
//     onClose(); // Close the dialog
//   };
//   useEffect(() => {
//     if (selected_ui_name) {
//       setSelectedComponent(selected_ui_name);
//       if (!isDailog) {
//         const selected = components.find((comp) => {
//           return comp.label === selected_ui_name;
//         });

//         onSelect(selected?.comp_data); // Return the selected component
//       }
//     }
//   }, [selected_ui_name]);

//   return (
//     <>
//       {isDailog ? (
//         <>
//           <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
//             <DialogTitle sx={{ backgroundColor: "#f5f6fa" }}>
//               Select a Design
//             </DialogTitle>
//             <DialogContent sx={{ backgroundColor: "#f5f6fa" }}>
//               <FormControl component="figure" fullWidth>
//                 <RadioGroup
//                   value={selectedComponent}
//                   onChange={handleSelectionChange}
//                   row // Display radio buttons horizontally,
//                   sx={{ zIndex: 10000 }}
//                 >
//                   <div
//                     style={{
//                       display: "flex",
//                       flexDirection: "row",
//                       justifyContent: "space-around",
//                       alignItems: "start",
//                       gap: 10,
//                     }}
//                   >
//                     {components.map(({ key, label, component: Component }) => (
//                       <Paper
//                         style={{ width: width ? width : "400px" }}
//                         onClick={() => setSelectedComponent(key)}
//                       >
//                         {Component}

//                         <FormControlLabel
//                           key={key}
//                           value={key}
//                           control={<Radio color="secondary" />}
//                           label={label}
//                           sx={{
//                             display: "flex",
//                             flexDirection: "column-reverse",
//                             alignItems: "center",
//                           }}
//                         />
//                       </Paper>
//                     ))}
//                   </div>
//                 </RadioGroup>
//               </FormControl>
//             </DialogContent>
//             <Box
//               sx={{
//                 display: "flex",
//                 justifyContent: "flex-end",
//                 p: 2,
//                 backgroundColor: "#f5f6fa",
//               }}
//             >
//               <Button
//                 onClick={onClose}
//                 color="secondary"
//                 variant="outlined"
//                 sx={{ mr: 1 }}
//               >
//                 Cancel
//               </Button>
//               <Button
//                 onClick={handleConfirm}
//                 color="primary"
//                 variant="contained"
//                 disabled={!selectedComponent}
//               >
//                 Confirm
//               </Button>
//             </Box>
//           </Dialog>
//         </>
//       ) : (
//         <>
//           <FormControl component="figure" fullWidth>
//             <RadioGroup
//               value={selectedComponent}
//               onChange={handleSelectionChange}
//               row // Display radio buttons horizontally,
//               sx={{ zIndex: 10000 }}
//             >
//               <div
//                 style={{
//                   display: "flex",
//                   flexDirection: "row",
//                   justifyContent: "space-around",
//                   alignItems: "start",
//                   gap: 10,
//                 }}
//               >
//                 {components.map(({ key, label, component: Component }) => (
//                   <Paper
//                     style={{ width: width ? width : "400px" }}
//                     onClick={() => setSelectedComponent(key)}
//                   >
//                     <FormControlLabel
//                       key={key}
//                       value={key}
//                       control={
//                         <Radio
//                           color="secondary"
//                           checked={selectedComponent == label}
//                           sx={{
//     color: "#1976d2", // blue when not selected
//     "&.Mui-checked input": {
//       color: "#1976d2", // blue when selected
//     },
//   }}
//                         />
//                       }
//                       // label={label}
//                       style={{
//                         margin: 0,
//                         display: "flex",
//                         justifyContent: "center",
//                       }}
//                     />
//                     {Component}
//                   </Paper>
//                 ))}
//               </div>
//             </RadioGroup>
//           </FormControl>
//         </>
//       )}
//     </>
//   );
// };

const AlertMessage = ({ open, onClose, message, severity, title }) => {
  return (
    <Snackbar
      open={open}
      autoHideDuration={4000}
      onClose={onClose}
      TransitionComponent={SlideTransition}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert
        onClose={onClose}
        severity={severity || "info"}
        sx={{ width: "100%" }}
        variant="filled"
      >
        {title && <strong>{title}</strong>}
        {title && message && <br />}
        {message}
      </Alert>
    </Snackbar>
  );
};

const BulletList = () => {
  const heading = "Pros and Cons of Working from Home";

  const bulletPoints = [
    <>
      <strong>Avoids commuting</strong>, saving both{" "}
      <strong>time and money</strong>
    </>,
    <>
      Many workers report <strong>increased productivity</strong> while working
      remotely
    </>,
    <>
      Companies benefit by <strong>reducing office costs</strong>
    </>,
    <>
      May lead to <strong>isolation</strong> and{" "}
      <strong>communication challenges</strong> if not managed well
    </>,
  ];

  return (
    <div className="max-w-xl mx-auto p-4 bg-white rounded shadow-md">
      <p style={{ color: "black", marginBottom: "20px" }}>Textual Format 1</p>
      <h6 style={{ color: "black", fontWeight: "bold" }}>Heading</h6>
      <ul className="list-disc pl-5 space-y-2">
        {bulletPoints.map((point, index) => (
          <li key={index} className="text-gray-800">
            {point}
          </li>
        ))}
      </ul>
    </div>
  );
};
